<template>
  <LoadingScreen :value="loadingLabel" />
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { cloneDeep, isEmpty, get } from "lodash";
import jwtdecode from "jwt-decode";
import notification from "@/utils/notifications";
import userMe from "@/services/user-me";

import LoadingScreen from "@/components/shared-components/LoadingScreen/LoadingScreen.vue";
import statesMFARequired from "@/constants/states-mfa-required";
import { getFromCookie } from "@/utils/cryptoUtils";
import { convertBase64ToFile } from "@/utils/file-reader-helper";
// CONSTANTS
import NOTIFICATION_CONSTANTS from "@/constants/notification-type-constants";

export default {
  name: "AuthCallback",
  components: {
    LoadingScreen,
  },
  data() {
    return {
      userData: null,
      eRxMFAStates: statesMFARequired.eRxMFA,
      loginMFAStates: statesMFARequired.loginMFA,
    };
  },
  computed: {
    ...mapGetters("publicAPIModule", ["getUserIdentity"]),
    ...mapGetters(["getSelectedCurrentLocationId"]),
    isMFARequired() {
      const userPrimaryState = (localStorage.getItem("userPrimaryState") || "").replace(/\s+/g, "");
      return userPrimaryState && this.eRxMFAStates.includes(userPrimaryState.toLowerCase());
    },
    loadingLabel() {
      const isFinalizeOrder = localStorage.getItem("finalizeOrder");
      const isEPCSIDProofing = localStorage.getItem("epcsIDProofing");
      if (isFinalizeOrder) {
        return "FINALIZING YOUR ORDER";
      }
      if (isEPCSIDProofing) {
        return "VALIDATING YOUR EPCS ID PROOFING";
      }
      return this.isMFARequired && localStorage.getItem("finalizeOnboarding") ? "FINALIZING THE ONBOARDING" : "LOADING";
    },
  },
  async created() {
    // ID.me opens in a popup. Hand the callback URL to the opener and stop here so
    // the already-authenticated parent finishes onboarding/login (avoids Auth0 init race in popup).
    // Scoped to IDMeCallback only — do not affect Auth0 /callback.
    if (this.$route.name === "IDMeCallback" && window.opener && !window.opener.closed) {
      window.opener.postMessage(window.location.href, window.location.origin);
      setTimeout(() => window.close(), 0);
      return;
    }

    const { isAuthenticated } = await this.$auth;
    const bioteAPIAccessToken = localStorage.getItem("access_token");
    const isEPCSIDProofing = localStorage.getItem("epcsIDProofing");
    const isFinalizeOrder = localStorage.getItem("finalizeOrder");

    const isInternalUser = await this.isInternalUser();

    if (isFinalizeOrder) {
      await this.proceedOrder();
      localStorage.removeItem("idmecode");
      return;
    }

    if (isEPCSIDProofing) {
      await this.idProofingProcess();
      localStorage.removeItem("idmecode");
      return;
    }

    if (isAuthenticated && !bioteAPIAccessToken) {
      if (isInternalUser) {
        await this.handleLogin({ isInternalUser });
        return;
      }
      const isFinalizeOnboarding = localStorage.getItem("finalizeOnboarding");

      const idMeResponse = await this.checkIDMeNavigation();
      const { navigateToIdMe, isUserOnboarded } = idMeResponse;

      if (idMeResponse && isFinalizeOnboarding && !isUserOnboarded && !isInternalUser) {
        await this.handleOnboarding();
        localStorage.removeItem("idmecode");
        return;
      }

      if (idMeResponse && !navigateToIdMe) {
        await this.handleLogin({ isUserOnboarded });
      }
    }
  },
  methods: {
    ...mapActions("publicAPIModule", ["fetchBioteUserDetails", "validateAuth0Token", "generateIDMeUrl", "verifyUser", "logoutUser", "onboardOrganization", "idProofUser"]),
    ...mapMutations("onboardingModule", ["appendDataToPracticeUserFormData", "setPreloadedData"]),
    ...mapActions("usersModule", ["initializeCurrentLoggedInInfo", "fetchLoggedUserAccessibleLocations", "initializeLoginDetails", "isInternalUser", "isNonPrescriberUser"]),
    ...mapMutations("locationsModule", ["setCurrentUserAvailableLocation"]),
    ...mapActions("ordersModule", ["placeOrder"]),
    ...mapActions("filesModule", ["uploadImgFile"]),
    ...mapActions("accountsModule", ["addLocationSignature"]),
    async handleOnboarding() {
      const idMeCode = this.getCodeFromUrl();
      const decodedPayload = getFromCookie("onboardingPayload");
      const base64String = localStorage.getItem("signatureImage");
      if (decodedPayload && base64String) {
        const file = convertBase64ToFile(base64String);
        const formData = new FormData();
        formData.append("image", file);
        decodedPayload.idMeCode = idMeCode || null;
        localStorage.removeItem("finalizeOnboarding");
        try {
          const response = await this.onboardOrganization(decodedPayload);
          if (response && response.data) {
            const accessToken = response.data.sessionObject.access_token;
            const decodedToken = await this.initializeLoginDetails(response.data.sessionObject);
            this.displayNotification("Successfully Onboarded!", `User has been onboarded successfully to ${decodedToken.sim.details.name} organization.`, "success");

            if (response.data.locationId) {
              await this.handleSignatureUpload(response.data.locationId, base64String);
            } else {
              this.displaySignatureError();
            }

            await this.initializeCurrentLoggedInInfo({
              access_token: accessToken,
              exp: decodedToken.exp,
              decodedToken,
            });
            this.navigateBasedOnRoles({ decodedToken });
          } else {
            await this.handleOnboardingError(response);
          }
        } catch (error) {
          console.error("Onboarding error: ", error);
          this.redirectToHome();
        }
      }
    },
    async handleOnboardingError(response) {
      if (response.npiError || !response.verified) {
        setTimeout(async () => {
          await this.logoutUser();
        }, 10000);
      } else {
        this.redirectToHome(3500);
      }
    },
    redirectToHome(delay = 0) {
      setTimeout(() => {
        window.location.href = "/";
      }, delay);
    },
    async handleSignatureUpload(locationId, base64String) {
      const file = convertBase64ToFile(base64String);
      const formData = new FormData();
      formData.append("image", file);

      try {
        const uploadResponse = await this.uploadImgFile({ formData });

        if (uploadResponse && uploadResponse.data) {
          const signatureFileLocation = get(uploadResponse, "data", "");
          await this.addLocationSignature({
            locationId,
            payload: { signatureFileLocation },
          });
        } else {
          this.displaySignatureError();
        }
      } catch (error) {
        console.error("Signature upload error: ", error);
        this.displaySignatureError();
      }
    },
    async displaySignatureError() {
      this.displayNotification("Failed to save due to error signature at this time", "Please contact BioteRx Customer Support at BioteRx@biote.com or 1-844-980-0683.");
    },
    displayNotification(message, description, type = "warning", duration = 4.5) {
      this.$notification[type]({
        message,
        description,
        class: `ant-notification-${type}-text`,
        duration,
      });
    },
    async proceedOrder() {
      const idMeCode = this.getCodeFromUrl();
      const decodedPayload = getFromCookie("prescriptionPayload");
      if (decodedPayload && decodedPayload.payload) {
        decodedPayload.payload.idMeCode = idMeCode || null;
        const {
          purchaseId,
          locationId,
          payload: { patientId },
          callbackUrl,
          successCallbackUrl,
        } = structuredClone(decodedPayload);
        const params = { locationId, patientId };
        const response = await this.placeOrder(decodedPayload);
        localStorage.removeItem("finalizeOrder");
        if (response) {
          this.$notification.success({
            message: "Thank you for your purchase!",
            description: (
              <div>
                Your prescription has been successfully completed.
                <br />
                <br />
                Order updates will be available on the patient card or the order history tab.
                <br />
                <br />
                If you have any questions or need further assistance, please contact our support team at BioteRx@biote.com or 844-980-0683.
                <br />
                <br />
                Have a great day!
              </div>
            ),
            class: "ant-notification-success-text",
          });
        }
        if (callbackUrl || successCallbackUrl) {
          this.$router.push({
            name: response ? successCallbackUrl : callbackUrl,
            params: {
              orderId: purchaseId,
              locationId,
            },
          });
          return;
        }
        this.$router.push({ name: "PatientDetails", params });
      }
    },
    async handleLogin({ isUserOnboarded, isInternalUser }) {
      const response = await this.validateAuth0();
      if (response) {
        const { data, loginDetails } = response;
        if ((isUserOnboarded || isInternalUser) && loginDetails) {
          this.handleSuccessfulLogin({ data, loginDetails, isInternalUser });
          return;
        }
        let navigateTo = "";
        // Onboarding Navigation
        if (!isInternalUser) {
          navigateTo = data && !data.orgOnboarded ? "PracticeInformation" : "PracticeUserAccountSetup";
          this.appendDataToPracticeUserFormData(data);
        } else {
          navigateTo = "VendorManagement";
        }
        // PracticeUserAccountSetup = Non-owner User Navigation
        this.$router.push({ name: navigateTo });
      }
    },
    async handleSuccessfulLogin({ data, loginDetails, isInternalUser }) {
      const decodedToken = await this.initializeLoginDetails(loginDetails);
      if (userMe.getMyRoles().isPatientUser) {
        notification("warning", "Unauthorized!", "Please log in as an organization user.");
        await this.logoutUser();
      } else {
        this.navigateBasedOnRoles({ data, decodedToken, isInternalUser });
      }
    },
    async generateAuth0Token() {
      const { getAccessTokenSilently } = await this.$auth;
      const auth0AccessToken = await getAccessTokenSilently();
      return auth0AccessToken;
    },
    async validateAuth0() {
      const auth0AccessToken = await this.generateAuth0Token();
      const code = this.getCodeFromUrl();
      const response = await this.validateAuth0Token({ accessToken: auth0AccessToken, idMeCode: code });
      return auth0AccessToken && response ? response : null;
    },
    async initializeLoginDetails(loginDetails) {
      // eslint-disable-next-line camelcase
      const { access_token } = loginDetails;
      const decodedToken = jwtdecode(access_token);
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("exp", decodedToken.exp);
      sessionStorage.setItem("access_token", access_token);
      sessionStorage.setItem("exp", decodedToken.exp);
      // eslint-disable-next-line camelcase
      await this.initializeCurrentLoggedInInfo({ access_token, exp: decodedToken.exp, decodedToken });
      return decodedToken;
    },
    async navigateBasedOnRoles({ data, decodedToken, isInternalUser }) {
      const { isVendorUser, isSimpatraAdmin } = userMe.getMyRoles();
      const selectedVendor = localStorage.getItem("selected_vendor");
      const callbackRoute = localStorage.getItem("callbackRoute");

      const hasSelectedVendor = Boolean(selectedVendor);
      const hasCallback = Boolean(callbackRoute);

      if (hasCallback) {
        this.navigateToCallback(callbackRoute);
        return;
      }

      const navigateToVendorDashboard = isInternalUser || isVendorUser || (isSimpatraAdmin && hasSelectedVendor);

      if (navigateToVendorDashboard) {
        if (!selectedVendor) {
          this.$router.push({ name: "VendorManagement" });
          return;
        }
        this.$router.push({
          name: "VendorOrderList",
          params: {
            vendorId: selectedVendor,
          },
        });
        return;
      }
      const { id } = decodedToken?.sim?.details || {};
      await this.navigatePracticeUser({ navigateToSettings: false, id });
    },
    async fetchUserLocations(currentOrgId) {
      const { isSimpatraAdmin } = userMe.getMyRoles();
      let orgId = localStorage.getItem("orgId") || currentOrgId;
      const hasSelectedOrg = !["", null, undefined].includes(orgId);
      // Reset orgId if current user is not a super admin
      orgId = hasSelectedOrg && isSimpatraAdmin ? orgId : currentOrgId;
      localStorage.setItem("orgId", orgId);

      // Fetch current logged in user accessible locations
      const queryParams = isSimpatraAdmin ? { orgId } : {};
      const userAccessibleLocations = await this.fetchLoggedUserAccessibleLocations(queryParams);
      const currentUserAvailableLocations = userAccessibleLocations?.locations || [];
      const userActiveLocations = currentUserAvailableLocations.filter(item => item.isActive === true);
      this.setCurrentUserAvailableLocation(currentUserAvailableLocations);

      let locationId = localStorage.getItem("currentLocation");
      const emptySelectedLocation = ["", null, undefined].includes(locationId);
      // Check current selected location if existed in available locations
      const isLocationExist = currentUserAvailableLocations.find(item => item.id === locationId);
      locationId = isEmpty(isLocationExist) || emptySelectedLocation ? userActiveLocations[0]?.id : locationId;
      localStorage.setItem("currentLocation", locationId);
      return locationId;
    },
    async navigatePracticeUser({ navigateToSettings, id }) {
      const orgId = id;
      const currentSelectedLocation = await this.fetchUserLocations(orgId);
      if (currentSelectedLocation) {
        this.$router.push(`/dashboard/${currentSelectedLocation}/patient-chart`);
      }
    },
    navigateToCallback(callbackRoute) {
      // Navigate to call back when email is still the same on the previous logged
      // Save email in localStorage
      this.$router.push({ path: callbackRoute });
      localStorage.removeItem("callbackRoute");
    },
    async checkIDMeNavigation() {
      try {
        // Generate Auth0 token and identify user for ID proofing
        if (!this.getUserIdentity) {
          await this.logoutUser();
          return;
        }

        // Extract necessary values from the response
        const isUserOnboarded = get(this.getUserIdentity, "isUserOnboarded", false);
        const requireIdProofing = get(this.getUserIdentity, "requireIdProofing", false);
        const primaryState = get(this.getUserIdentity, "primaryState", "");

        localStorage.removeItem("userPrimaryState");
        // Store primary state in local storage
        localStorage.setItem("userPrimaryState", primaryState);

        const userPrimaryState = (primaryState || "").replace(/\s+/g, "");
        // Determine if MFA is required based on primary state (e.g. NY requires ID.me on login)
        const isMFARequired = userPrimaryState && this.loginMFAStates.includes(userPrimaryState.toLowerCase());

        // Non-prescribers from ERx states (e.g. NY) should not require ID.me on login.
        // Prescriber = has Provider, ProviderFinancials, or BioteSales Auth0 role (same as AccountSetup/OnboardingForm isNonPrescriberUser).
        let isPrescriber = true;
        try {
          const isNonPrescriber = await this.isNonPrescriberUser();
          isPrescriber = !isNonPrescriber;
        } catch (e) {
          // Default to requiring ID.me if role check fails (safe default)
        }

        // Determine the current route
        const isNotIDMeCallbackRoute = this.$route.name !== "IDMeCallback";

        // Only require ID.me on login for prescribers in login-MFA states (e.g. NY). Non-prescribers are excluded.
        if (isMFARequired && isUserOnboarded && isNotIDMeCallbackRoute && isPrescriber) {
          const idMeUrl = await this.generateIDMeUrl();
          window.location.href = idMeUrl;
          return { navigateToIdMe: true };
        }
        this.userData = { primaryState, requireIdProofing, isUserOnboarded };
        return { navigateToIdMe: false, isUserOnboarded };
      } catch (error) {
        const isUserOnboarded = get(this.getUserIdentity, "isUserOnboarded", false);
        console.error("Error in checkIDMeNavigation:", error);
        return { navigateToIdMe: false, isUserOnboarded: false };
      }
    },
    getCodeFromUrl() {
      if (this.$route.name === "IDMeCallback") {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get("error") || null;
        if (error === "access_denied") {
          return 401;
        }
        const idMeCode = urlParams.get("code") || null;
        if (idMeCode === null) {
          notification(NOTIFICATION_CONSTANTS.ERROR, "ID.me Unauthorized!", "Token not found!");
        }
        return idMeCode;
      }
      return null;
    },
    getErrorFromURL() {
      if (this.$route.name === "IDMeCallback") {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("error") || null;
      }
      return null;
    },
    async idProofingProcess() {
      const idMeCode = this.getCodeFromUrl();
      const locationdId = this.getSelectedCurrentLocationId;
      await this.idProofUser({
        locationId: this.getSelectedCurrentLocationId,
        isAccepted: true,
        idMeCode,
      });
      localStorage.removeItem("epcsIDProofing");
      this.$router.push(`/dashboard/${locationdId}/patient-chart`);
    },
  },
};
</script>

<style type="scss" scoped></style>
